import { Link, NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { ScrollToTop } from "@/components/ScrollToTop";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { ShoppingCart, User, LayoutDashboard, LogOut, Layers, Menu, X } from "lucide-react";
import { CartDrawer } from "@/components/CartDrawer";
import { useBusinessContact } from "@/lib/whatsapp";
import { InstallPrompt } from "@/components/InstallPrompt";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { KioskGuard, useKiosk } from "@/components/KioskGuard";
import { KioskExit } from "@/components/KioskExit";
import { ServicesNav, ServicesMobileNav } from "@/components/ServicesNav";

const NAV = [
  { to: "/builder", label: "Gang Sheet Builder" },
  { to: "/request-design", label: "Request a Design" },
  { to: "/order-labels", label: "Order Labels & Stickers" },
  { to: "/catalog", label: "Design Catalog" },
  { to: "/merch", label: "Merchandise" },
  { to: "/collections", label: "Collections" },
  { to: "/track", label: "Track Order" },
  { to: "/reviews", label: "Reviews" },
];

export default function Layout() {
  const contact = useBusinessContact();
  const biz = contact.business || {};
  const headerRef = useRef(null);
  const kiosk = useKiosk();
  useEffect(() => {
    const el = headerRef.current; if (!el) return;
    const set = () => document.documentElement.style.setProperty("--header-h", `${el.offsetHeight}px`);
    set(); const ro = new ResizeObserver(set); ro.observe(el); return () => ro.disconnect();
  }, [kiosk]);
  const { data: faqData } = useQuery({ queryKey: ["public-faq"], queryFn: async () => (await api.get("/public/faq")).data, staleTime: 60000 });
  const faq = faqData || { turnaround_days: 3, turnaround_max_shirts: 5, rush_available: true, services_yes: [{ title: "DTF for tees, hoodies, caps & uniforms" }, { title: "Sublimation for personalised gifts" }], services_no: [{ title: "Sublimation tees" }] };
  const { user, logout } = useAuth();
  const { count, setDrawerOpen } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [menuOpen]);

  if (kiosk) {
    return (
      <div className="min-h-screen flex flex-col bg-[#0c0d10]" data-testid="kiosk-layout">
        <header ref={headerRef} className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0c0d10]/90 border-b border-zinc-800/80" data-testid="kiosk-header">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-20 flex items-center justify-between gap-4">
            <Link to="/vendor" className="flex items-center gap-2 shrink-0" data-testid="kiosk-brand">
              <img src="/logo-mark.png?v=5" alt="All-Ways Graphic" className="h-12 lg:h-16 w-auto object-contain" />
              <span className="font-display font-extrabold text-lg lg:text-xl tracking-tight text-white whitespace-nowrap">ALL-WAYS<span className="text-[#ff6a13]"> GRAPHIC</span><span className="hidden sm:inline text-zinc-300 font-mono-p text-xs font-normal ml-3 tracking-widest uppercase">Vendor Market</span></span>
            </Link>
            <div className="flex items-center gap-2">
              <button onClick={() => navigate("/vendor/checkout")} className="relative p-2.5 rounded-lg hover:bg-zinc-900 text-zinc-300" data-testid="kiosk-cart-btn">
                <ShoppingCart size={20} />
                {count > 0 && <span className="absolute -top-0.5 -right-0.5 bg-[#ff6a13] text-white text-[10px] font-bold min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center font-mono-p">{count}</span>}
              </button>
              <KioskExit compact />
            </div>
          </div>
          <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg,#ff6a13,#ffd23f,#4fd23a,#22c3d6,#ff2fb0)" }} />
        </header>
        <main className="flex-1" style={{ paddingTop: "var(--header-h, 64px)" }}><ScrollToTop /><KioskGuard><Outlet /></KioskGuard></main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-[#0c0d10]">
      <InstallPrompt />
      <header ref={headerRef} className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0c0d10]/85 border-b border-zinc-800/80" data-testid="site-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 lg:h-24 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
            <img src="/logo-mark.png?v=5" alt="All-Ways Graphic" className="h-12 lg:h-[76px] w-auto object-contain" />
            <span className="font-display font-extrabold text-lg lg:text-xl tracking-tight text-white whitespace-nowrap hidden sm:inline">ALL-WAYS<span className="text-[#ff6a13]"> GRAPHIC</span></span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            <ServicesNav />
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} data-testid={`nav-${n.to.slice(1)}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-sm font-medium rounded-lg transition-colors ${isActive ? "text-white bg-zinc-800/70" : "text-zinc-400 hover:text-white hover:bg-zinc-900"}`}>
                {n.label}
              </NavLink>
            ))}
            {user?.role === "admin" && (
              <Link to="/admin?tab=sheets" data-testid="nav-library" className="px-3 py-2 text-sm font-medium rounded-lg transition-colors text-[#22c3d6] hover:text-white hover:bg-zinc-900">Library</Link>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <button onClick={() => setMenuOpen((o) => !o)} aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} className="lg:hidden p-2.5 rounded-lg hover:bg-zinc-900 text-zinc-200" data-testid="mobile-menu-btn">
              {menuOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <button onClick={() => setDrawerOpen(true)} className="relative p-2.5 rounded-lg hover:bg-zinc-900 text-zinc-300" data-testid="cart-open-btn">
              <ShoppingCart size={20} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#ff6a13] text-white text-[10px] font-bold w-4.5 h-4.5 min-w-[18px] h-[18px] px-1 rounded-full flex items-center justify-center font-mono-p" data-testid="cart-count">{count}</span>
              )}
            </button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full bg-zinc-900 border border-zinc-800 hover:border-zinc-700" data-testid="account-menu-btn">
                    {user.picture ? <img src={user.picture} alt="" className="w-6 h-6 rounded-full" /> : <User size={16} className="text-zinc-300" />}
                    <span className="text-sm text-zinc-200 max-w-[90px] truncate hidden sm:block">{user.name || user.email}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#1e222d] border-zinc-800 text-zinc-200">
                  <DropdownMenuItem onClick={() => navigate("/account")} data-testid="menu-account"><User size={15} className="mr-2" />My Orders</DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin"><LayoutDashboard size={15} className="mr-2" />Admin Dashboard</DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout"><LogOut size={15} className="mr-2" />Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Button onClick={() => navigate("/login")} data-testid="login-btn" className="bg-white text-black hover:bg-zinc-200 rounded-full font-semibold h-9">Sign In</Button>
            )}
          </div>
        </div>
        <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg,#ff6a13,#ffd23f,#4fd23a,#22c3d6,#ff2fb0)" }} />
      </header>
      {menuOpen && (
        <div className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-[#0c0d10]/95 backdrop-blur-xl overflow-y-auto" style={{ top: "var(--header-h, 64px)" }} data-testid="mobile-menu" onClick={() => setMenuOpen(false)}>
            <nav className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
              {NAV.map((n, i) => (
                <NavLink key={n.to} to={n.to} data-testid={`mobile-nav-${n.to.slice(1)}`} style={{ animationDelay: `${i * 40}ms` }}
                  className={({ isActive }) => `animate-fade-up px-4 py-3.5 text-base font-semibold rounded-xl border transition-colors ${isActive ? "text-white bg-zinc-800/70 border-[#ff6a13]/60" : "text-zinc-200 border-zinc-800/80 hover:bg-zinc-900 hover:border-zinc-700"}`}>
                  {n.label}
                </NavLink>
              ))}
              <ServicesMobileNav />
              {user?.role === "admin" && <Link to="/admin" data-testid="mobile-nav-admin" className="px-4 py-3.5 text-base font-semibold rounded-xl border border-[#22c3d6]/40 text-[#22c3d6] hover:bg-zinc-900">Admin Dashboard</Link>}
              {!user && <Button onClick={() => navigate("/login")} data-testid="mobile-login-btn" className="mt-3 bg-white text-black hover:bg-zinc-200 rounded-full font-semibold h-11">Sign In</Button>}
            </nav>
          </div>
        )}

      <main className="flex-1" style={{ paddingTop: "var(--header-h, 64px)" }}><ScrollToTop /><Outlet /></main>
      <CartDrawer />

      <footer className="border-t-0 bg-[#0c0d10] mt-20">
        <div className="h-[3px] w-full" style={{ background: "linear-gradient(90deg,#ff2fb0,#22c3d6,#4fd23a,#ffd23f,#ff6a13)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 flex flex-col lg:flex-row justify-between gap-8">
          <div>
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png?v=5" alt="All-Ways Graphic" className="h-16 w-auto object-contain" />
              <span className="font-display font-extrabold text-lg tracking-tight text-white whitespace-nowrap">ALL-WAYS<span className="text-[#ff6a13]"> GRAPHIC</span></span>
            </div>
            <p className="text-zinc-500 text-sm mt-3 max-w-xs" data-testid="footer-tagline">{biz.tagline || "Precision DTF gang sheets and custom apparel, pressed with laser accuracy."}</p>
            {(biz.address || biz.hours || biz.email) && (
              <div className="text-xs text-zinc-500 mt-3 space-y-1 font-mono-p" data-testid="footer-contact">
                {biz.address && <p>{biz.address}</p>}
                {biz.hours && <p>{biz.hours}</p>}
                {biz.email && <p><a href={`mailto:${biz.email}`} className="hover:text-white">{biz.email}</a> · WhatsApp {contact.whatsapp_display}</p>}
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-10 text-sm">
            <div className="flex flex-col gap-2">
              {NAV.map((n) => <Link key={n.to} to={n.to} className="text-zinc-400 hover:text-white">{n.label}</Link>)}
              <Link to="/artwork-guidelines" data-testid="footer-artwork-guidelines" className="text-[#22c3d6] hover:text-white">Artwork Guidelines</Link>
              <Link to="/faq" data-testid="footer-faq" className="text-[#22c3d6] hover:text-white">FAQ</Link>
            </div>
            <div className="max-w-xs" data-testid="footer-faq-summary">
              <p className="text-xs font-mono-p uppercase tracking-wider text-zinc-500 mb-2">Quick FAQ</p>
              <ul className="space-y-1.5 text-xs text-zinc-400 leading-relaxed">
                <li><span className="text-zinc-200">Turnaround:</span> {faq.turnaround_days} days for up to {faq.turnaround_max_shirts} shirts from artwork approval. Larger orders: delivery time agreed with you. {faq.rush_available ? <span className="text-[#4fd23a]" data-testid="footer-rush-on">Next-day rush available this week.</span> : <span data-testid="footer-rush-off">Next-day rush not available this week.</span>}</li>
                <li><span className="text-zinc-200">Files:</span> PNG, JPEG or AI with transparent background. Screenshots — we try to clean, no guarantee. Everything sent for your approval before printing.</li>
                <li><span className="text-zinc-200">Services:</span> {(faq.services_yes || []).map((s) => s.title).join(" · ")}{(faq.services_no || []).length ? <> — <span className="text-[#ff2fb0]">no {(faq.services_no || []).map((s) => s.title.toLowerCase()).join(", ")}</span></> : null}.</li>
              </ul>
              <Link to="/faq" className="inline-block mt-2 text-xs text-[#22c3d6] hover:text-white hover:underline underline-offset-2">Read the full FAQ →</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-zinc-800/60 py-4 text-center text-xs text-zinc-600 font-mono-p">© {new Date().getFullYear()} {biz.name || "All-Ways Graphic"} · All rights reserved</div>
      </footer>
    </div>
  );
}
